import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, UserPlus, StickyNote, Pencil, Megaphone, FileDown, Clock, Sparkles, Ghost, Eye, ScanLine, ChevronLeft, ChevronRight } from "lucide-react";
import { FollowUpDialog } from "@/components/FollowUpDialog";
import { TearSheetDialog } from "@/components/TearSheetDialog";
import { TermsheetXray } from "@/components/TermsheetXray";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { PendingFollowersBanner } from "@/components/PendingFollowersBanner";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";

const STAGE_LABELS = {
  researching: "Researching", contacted: "Contacted", meeting: "Meeting",
  diligence: "Diligence", committed: "Committed", passed: "Passed",
};
const STAGE_COLORS = {
  researching: "bg-slate-100 text-slate-700", contacted: "bg-sky-50 text-sky-800",
  meeting: "bg-brand/10 text-brand-ink", diligence: "bg-amber-50 text-amber-800",
  committed: "bg-emerald-50 text-emerald-700", passed: "bg-red-50 text-red-600",
};
const EMPTY_FORM = { name: "", firm: "", email: "", stage: "researching", check_size: "", source: "", next_step: "", next_follow_up: "" };

const GRADE_STYLES = {
  responsive: "bg-emerald-50 text-emerald-700", warm: "bg-emerald-50 text-emerald-700",
  ghosting: "bg-amber-50 text-amber-800", cold: "bg-slate-100 text-slate-500",
  passed: "bg-red-50 text-red-600", committed: "bg-emerald-50 text-emerald-700",
};

const daysAgo = (iso) => {
  if (!iso) return "never";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "today" : d === 1 ? "1d ago" : `${d}d ago`;
};

function GhostDetector({ rows, prospects, onFollowUp }) {
  const worst = rows.filter((r) => ["ghosting", "cold"].includes(r.grade)).slice(0, 5);
  if (worst.length === 0) return null;
  return (
    <Card className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="ghost-detector">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-amber-900">
        <Ghost className="h-4 w-4" /> Ghost detector
      </div>
      <p className="mb-3 text-xs text-muted-foreground">Investors going dark, ranked by responsiveness. Stop chasing the coldest, nudge the rest.</p>
      <div className="space-y-1.5">
        {worst.map((r) => (
          <div key={r.prospect_id} className="flex flex-wrap items-center gap-2" data-testid={`ghost-row-${r.prospect_id}`}>
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${GRADE_STYLES[r.grade]}`}>{r.grade}</span>
            <span className="text-xs font-semibold text-foreground">{r.name}</span>
            {r.firm && <span className="text-[11px] text-muted-foreground">{r.firm}</span>}
            <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><Eye className="h-3 w-3" /> {r.deck_views} view{r.deck_views === 1 ? "" : "s"}</span>
            <span className="text-[11px] text-muted-foreground">active {daysAgo(r.last_activity_at)}</span>
              <span className="ff-mono ml-auto text-[10px] font-bold text-amber-800">{r.score}/100</span>
              <Button size="sm" variant="outline" className="h-6 gap-1 border-amber-300 px-2 text-[11px]"
              onClick={() => onFollowUp(prospects.find((p) => p.id === r.prospect_id))} data-testid={`ghost-draft-${r.prospect_id}`}>
              <Sparkles className="h-3 w-3" /> Nudge
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

const ANNOUNCE_TEMPLATES = [
  { kind: "round", label: "Round closed", headline: "Seed round", message: "We're thrilled to share that we've closed our round. Thank you for your support and belief in the mission. More to come in our next update." },
  { kind: "hire", label: "Key hire", headline: "We hired a VP of Engineering", message: "We're excited to welcome a key addition to the leadership team. This hire accelerates our roadmap significantly." },
  { kind: "customer", label: "Major customer", headline: "We signed a flagship customer", message: "We just signed one of the biggest names in our target market. This validates our positioning and opens the door to similar logos." },
  { kind: "launch", label: "Product launch", headline: "We launched our new product", message: "Today we shipped something we've been building for months. Early feedback has been outstanding and we'd love your amplification." },
];

export default function Fundraising() {
  const { company } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [announceOpen, setAnnounceOpen] = useState(false);
  const [aForm, setAForm] = useState({ round_name: "", amount: "", lead_investor: "", message: "", include_prospects: false });
  const [announcing, setAnnouncing] = useState(false);
  const [followupProspect, setFollowupProspect] = useState(null);
  const [tearOpen, setTearOpen] = useState(false);
  const [xrayOpen, setXrayOpen] = useState(false);
  const [engagement, setEngagement] = useState(null);
  const boardRef = useRef(null);
  const [scroll, setScroll] = useState({ left: false, right: false });
  const updateScroll = () => {
    const el = boardRef.current;
    if (!el) return;
    setScroll({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  };
  const scrollBoard = (dx) => boardRef.current?.scrollBy({ left: dx, behavior: "smooth" });
  useEffect(() => {
    updateScroll();
    const el = boardRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScroll);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  const load = async () => {
    if (!company) return;
    try { const { data } = await api.get(`/companies/${company.id}/prospects`); setData(data); }
    catch (e) { console.error("Failed to load prospects", e); }
    finally { setLoading(false); }
    api.get(`/companies/${company.id}/prospect-engagement`).then(({ data }) => setEngagement(data)).catch(() => {});
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, firm: p.firm || "", email: p.email || "", stage: p.stage,
      check_size: p.check_size ?? "", source: p.source || "", next_step: p.next_step || "", next_follow_up: p.next_follow_up || "" });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, check_size: form.check_size === "" ? null : Number(form.check_size) };
    try {
      if (editing) await api.put(`/prospects/${editing.id}`, payload);
      else await api.post(`/companies/${company.id}/prospects`, payload);
      toast.success(editing ? "Prospect updated" : "Prospect added");
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save prospect"); }
    finally { setSaving(false); }
  };

  const moveStage = async (p, stage) => {
    try {
      await api.put(`/prospects/${p.id}`, { name: p.name, firm: p.firm, email: p.email, stage,
        check_size: p.check_size, source: p.source, next_step: p.next_step, next_follow_up: p.next_follow_up });
      toast.success(`${p.name} moved to ${stage.replace(/_/g, " ")}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not move prospect"); }
  };

  const remove = async (p) => {
    try { await api.delete(`/prospects/${p.id}`); toast.success("Prospect removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const convert = async (p) => {
    try { await api.post(`/prospects/${p.id}/convert`); toast.success(`${p.name} added as investor`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not convert"); }
  };

  const addNote = async () => {
    if (!note.trim() || !editing) return;
    try {
      const { data } = await api.post(`/prospects/${editing.id}/notes`, { text: note.trim() });
      setEditing((p) => ({ ...p, notes: [...(p.notes || []), data] }));
      setNote("");
      load();
    } catch { toast.error("Could not add note"); }
  };

  const announce = async (e) => {
    e.preventDefault();
    setAnnouncing(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/announce-round`, {
        ...aForm, amount: aForm.amount === "" ? null : Number(aForm.amount),
      });
      toast.success(`Announcement sent to ${data.recipients} recipient${data.recipients === 1 ? "" : "s"}`);
      setAnnounceOpen(false);
      setAForm({ kind: "round", round_name: "", amount: "", lead_investor: "", message: "", include_prospects: false });
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send announcement"); }
    finally { setAnnouncing(false); }
  };

  const applyTemplate = (kind) => {
    const tpl = ANNOUNCE_TEMPLATES.find((t) => t.kind === kind);
    if (!tpl) { setAForm((f) => ({ ...f, kind })); return; }
    setAForm((f) => ({ ...f, kind: tpl.kind, round_name: tpl.headline, message: tpl.message }));
  };

  const stages = data?.stages || Object.keys(STAGE_LABELS);
  const prospects = data?.prospects || [];
  const gradeById = {};
  (engagement?.rows || []).forEach((r) => { gradeById[r.prospect_id] = r; });

  return (
    <AppShell title="Fundraising" subtitle="Prospective investor pipeline"
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setXrayOpen(true)} className="gap-2" data-testid="xray-open-btn">
            <ScanLine className="h-4 w-4" /> X-Ray
          </Button>
          <Button variant="outline" onClick={() => setTearOpen(true)} className="gap-2" data-testid="tear-sheet-btn">
            <FileDown className="h-4 w-4" /> Tear sheet
          </Button>
          <Button variant="outline" onClick={() => setAnnounceOpen(true)} className="gap-2" data-testid="announce-round-btn"><Megaphone className="h-4 w-4" /> Announce</Button>
          <Button onClick={openNew} className="gap-2" data-testid="prospect-add-btn"><Plus className="h-4 w-4" /> Add prospect</Button>
        </div>
      }>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Card className="rounded-xl border bg-card p-4" data-testid="funnel-total">
              <div className="text-[11px] text-muted-foreground">Prospects</div>
              <div className="h-display text-2xl font-semibold tabular-nums">{data?.total ?? 0}</div>
            </Card>
            <Card className="rounded-xl border bg-card p-4" data-testid="funnel-meetings">
              <div className="text-[11px] text-muted-foreground">In meetings +</div>
              <div className="h-display text-2xl font-semibold tabular-nums">{(data?.funnel?.meeting || 0) + (data?.funnel?.diligence || 0)}</div>
            </Card>
            <Card className="rounded-xl border bg-card p-4" data-testid="funnel-committed">
              <div className="text-[11px] text-muted-foreground">Committed</div>
              <div className="h-display text-2xl font-semibold tabular-nums">{data?.funnel?.committed || 0}</div>
            </Card>
            <Card className="rounded-xl border bg-card p-4" data-testid="funnel-committed-total">
              <div className="text-[11px] text-muted-foreground">Committed $</div>
              <div className="h-display text-2xl font-semibold tabular-nums text-primary">{formatCurrency(data?.committed_total || 0)}</div>
            </Card>
          </div>

          <PendingFollowersBanner companyId={company?.id} onAdded={load} />

          {engagement && <GhostDetector rows={engagement.rows} prospects={prospects} onFollowUp={setFollowupProspect} />}

          {(data?.stale_count || 0) > 0 && (
            <Card className="mb-6 rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="stale-banner">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900">
                <Clock className="h-4 w-4" /> {data.stale_count} prospect{data.stale_count === 1 ? "" : "s"} going quiet
              </div>
              <div className="space-y-1.5">
                {prospects.filter((p) => p.stale).map((p) => (
                  <div key={p.id} className="flex flex-wrap items-center gap-2" data-testid={`stale-row-${p.id}`}>
                    <span className="text-xs font-medium text-amber-950">{p.name}</span>
                    {p.firm && <span className="text-[11px] text-amber-800/70">{p.firm}</span>}
                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${STAGE_COLORS[p.stage]}`}>{STAGE_LABELS[p.stage]}</span>
                    <Button size="sm" variant="outline" className="ml-auto h-6 gap-1 border-amber-300 px-2 text-[11px]" onClick={() => setFollowupProspect(p)} data-testid={`stale-draft-${p.id}`}>
                      <Sparkles className="h-3 w-3" /> Draft follow-up
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <div className="relative" data-testid="pipeline-board-wrap">
            {scroll.right && (
              <p className="mb-1.5 text-right text-[11px] font-medium text-muted-foreground" data-testid="pipeline-scroll-hint">
                Scroll sideways for more stages <ChevronRight className="inline h-3 w-3" />
              </p>
            )}
            {scroll.left && <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-background to-transparent" />}
            {scroll.right && <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-background to-transparent" data-testid="pipeline-scroll-fade" />}
            {scroll.left && (
              <button type="button" onClick={() => scrollBoard(-320)} aria-label="Scroll left"
                className="absolute left-2 top-1/2 z-20 -translate-y-1/2 rounded-full border bg-card p-2 text-foreground shadow-md transition-colors hover:bg-secondary" data-testid="pipeline-scroll-left">
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            {scroll.right && (
              <button type="button" onClick={() => scrollBoard(320)} aria-label="Scroll right"
                className="absolute right-2 top-1/2 z-20 -translate-y-1/2 rounded-full border bg-card p-2 text-foreground shadow-md transition-colors hover:bg-secondary" data-testid="pipeline-scroll-right">
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          <div ref={boardRef} onScroll={updateScroll} className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
            {stages.map((stage) => {
              const col = prospects.filter((p) => p.stage === stage);
              return (
                <div key={stage} className="w-[260px] shrink-0" data-testid={`pipeline-col-${stage}`}>
                  <div className="mb-2 flex items-center justify-between px-1">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STAGE_COLORS[stage]}`}>{STAGE_LABELS[stage]}</span>
                    <span className="text-xs tabular-nums text-muted-foreground">{col.length}</span>
                  </div>
                  <div className="space-y-2">
                    {col.map((p) => (
                      <Card key={p.id} className="rounded-lg border bg-card p-3" data-testid={`prospect-card-${p.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-foreground">{p.name}</div>
                            {p.firm && <div className="truncate text-xs text-muted-foreground">{p.firm}</div>}
                          </div>
                          <button onClick={() => openEdit(p)} className="text-muted-foreground hover:text-foreground" data-testid={`prospect-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                        </div>
                        {p.check_size != null && <div className="mt-1.5 text-xs font-semibold tabular-nums text-primary">{formatCurrency(p.check_size)}</div>}
                        {gradeById[p.id] && ["ghosting", "responsive"].includes(gradeById[p.id].grade) && (
                          <span className={`mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${GRADE_STYLES[gradeById[p.id].grade]}`} data-testid={`prospect-grade-${p.id}`}>
                            {gradeById[p.id].grade === "ghosting" && <Ghost className="h-2.5 w-2.5" />}{gradeById[p.id].grade}
                          </span>
                        )}
                        {gradeById[p.id]?.response === "interested" && (
                            <span className="mt-1 ml-1 inline-flex items-center rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700" data-testid={`prospect-interested-${p.id}`}>wants updates</span>
                        )}
                        {gradeById[p.id]?.on_tenx && (
                          <span className="mt-1 ml-1 inline-flex items-center rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-700" title="Has a tenx investor account: add them as an investor to share updates" data-testid={`prospect-on-tenx-${p.id}`}>on tenx</span>
                        )}
                        {p.source && <div className="mt-1 truncate text-[11px] text-muted-foreground" data-testid={`prospect-source-${p.id}`}>Source: {p.source}</div>}
                        {p.next_step && <div className="mt-1 truncate text-[11px] text-muted-foreground">Next: {p.next_step}</div>}
                        {(p.notes || []).length > 0 && (
                          <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground"><StickyNote className="h-3 w-3" />{p.notes.length} note{p.notes.length === 1 ? "" : "s"}</div>
                        )}
                        <div className="mt-2 flex items-center gap-1.5">
                          <Select value={p.stage} onValueChange={(v) => moveStage(p, v)}>
                            <SelectTrigger className="h-7 flex-1 text-xs" data-testid={`prospect-stage-${p.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {stages.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-600" onClick={() => remove(p)} data-testid={`prospect-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                        {p.stage === "committed" && !p.converted_investor_id && (
                          <Button size="sm" variant="outline" className="mt-2 w-full gap-1.5 text-xs" onClick={() => convert(p)} data-testid={`prospect-convert-${p.id}`}>
                            <UserPlus className="h-3.5 w-3.5" /> Convert to investor
                          </Button>
                        )}
                        {p.converted_investor_id && <Badge className="mt-2 border-emerald-200 bg-emerald-50 text-emerald-700">Investor onboarded</Badge>}
                      </Card>
                    ))}
                    {col.length === 0 && <div className="rounded-lg border border-dashed p-3 text-center text-xs text-muted-foreground">Empty</div>}
                  </div>
                </div>
              );
            })}
          </div>
          </div>
        </>
      )}

      <FollowUpDialog prospect={followupProspect} onClose={() => setFollowupProspect(null)} onSent={load} />
      <TearSheetDialog open={tearOpen} onClose={() => setTearOpen(false)} />
      {company && <TermsheetXray open={xrayOpen} onClose={() => setXrayOpen(false)} companyId={company.id} />}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit prospect" : "Add prospect"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required className="mt-1" data-testid="prospect-form-name" /></div>
              <div><Label>Firm</Label><Input value={form.firm} onChange={(e) => setForm((f) => ({ ...f, firm: e.target.value }))} className="mt-1" data-testid="prospect-form-firm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="mt-1" data-testid="prospect-form-email" /></div>
              <div><Label>Check size ($)</Label><Input type="number" value={form.check_size} onChange={(e) => setForm((f) => ({ ...f, check_size: e.target.value }))} className="mt-1" data-testid="prospect-form-check" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Stage</Label>
                <Select value={form.stage} onValueChange={(v) => setForm((f) => ({ ...f, stage: v }))}>
                  <SelectTrigger className="mt-1" data-testid="prospect-form-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STAGE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Source</Label><Input value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} placeholder="Warm intro, cold…" className="mt-1" data-testid="prospect-form-source" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Next step</Label><Input value={form.next_step} onChange={(e) => setForm((f) => ({ ...f, next_step: e.target.value }))} className="mt-1" data-testid="prospect-form-next" /></div>
              <div><Label>Follow up on</Label><DatePicker value={form.next_follow_up} onChange={(v) => setForm((f) => ({ ...f, next_follow_up: v }))} className="mt-1" data-testid="prospect-form-followup" /></div>
            </div>
            <Button type="submit" disabled={saving} className="w-full gap-2" data-testid="prospect-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {editing ? "Save changes" : "Add prospect"}
            </Button>
          </form>
          {editing && (
            <div className="border-t pt-3">
              <Label className="text-xs">Notes</Label>
              <div className="mt-2 max-h-36 space-y-2 overflow-y-auto">
                {(editing.notes || []).map((n) => (
                  <div key={n.id} className="rounded-md bg-secondary/60 p-2 text-xs">
                    <div className="text-foreground">{n.text}</div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">{n.author}</div>
                  </div>
                ))}
                {(editing.notes || []).length === 0 && <p className="text-xs text-muted-foreground">No notes yet.</p>}
              </div>
              <div className="mt-2 flex gap-2">
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className="h-8 text-sm" data-testid="prospect-note-input" />
                <Button size="sm" variant="outline" onClick={addNote} data-testid="prospect-note-add">Add</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={announceOpen} onOpenChange={setAnnounceOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Announce a milestone</DialogTitle>
            <DialogDescription>Emails every investor a branded announcement.</DialogDescription>
          </DialogHeader>
          <form onSubmit={announce} className="space-y-3">
            <div>
              <Label>Template</Label>
              <Select value={aForm.kind} onValueChange={applyTemplate}>
                <SelectTrigger className="mt-1" data-testid="announce-template"><SelectValue /></SelectTrigger>
                <SelectContent>{ANNOUNCE_TEMPLATES.map((t) => <SelectItem key={t.kind} value={t.kind}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Headline</Label><Input value={aForm.round_name} onChange={(e) => setAForm((f) => ({ ...f, round_name: e.target.value }))} placeholder="Seed round" required className="mt-1" data-testid="announce-round-name" /></div>
              <div><Label>Amount raised ($)</Label><Input type="number" value={aForm.amount} onChange={(e) => setAForm((f) => ({ ...f, amount: e.target.value }))} disabled={aForm.kind !== "round"} className="mt-1" data-testid="announce-amount" /></div>
            </div>
            {aForm.kind === "round" && (
              <div><Label>Lead investor</Label><Input value={aForm.lead_investor} onChange={(e) => setAForm((f) => ({ ...f, lead_investor: e.target.value }))} className="mt-1" data-testid="announce-lead" /></div>
            )}
            <div><Label>Personal message</Label><Textarea value={aForm.message} onChange={(e) => setAForm((f) => ({ ...f, message: e.target.value }))} rows={3} placeholder="A few words of thanks and what's next…" className="mt-1" data-testid="announce-message" /></div>
            <label className="flex items-center gap-2 text-xs text-foreground">
              <Checkbox checked={aForm.include_prospects} onCheckedChange={(v) => setAForm((f) => ({ ...f, include_prospects: !!v }))} data-testid="announce-include-prospects" />
              Also send to pipeline prospects with an email address
            </label>
            <Button type="submit" disabled={announcing} className="w-full gap-2" data-testid="announce-send">
              {announcing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />} Send announcement
            </Button>
            <p className="text-xs text-muted-foreground">Sends a branded email to all investors{aForm.include_prospects ? " and prospects" : ""} plus an in-app notification.</p>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

